/*
 * <pdp-jump-nav> — anchor links in the PDP sticky bar.
 *
 * Each <a data-jump-target="<selector list>"> scrolls to the first matching
 * section. A link whose section isn't on this template is removed, so the bar
 * never offers a dead jump (section order differs per product template).
 *
 * A custom element, not a DOMContentLoaded script: the sticky bar sits inside a
 * [data-variant-refresh] region, so it is replaced on every variant swap and
 * markup adopted from DOMParser never runs its inline scripts.
 */
class PdpJumpNav extends HTMLElement {
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll("[data-jump-target]"));
    if (!this.links.length) return;

    this._onClick = this._handleClick.bind(this);

    this.links.forEach((link) => {
      const target = this._resolve(link);
      if (!target) {
        link.remove();
        return;
      }
      // Section ids are template-scoped (shopify-section-template--123__specs),
      // so the real href is only knowable at runtime. The markup ships href="#".
      if (target.id) link.setAttribute("href", `#${target.id}`);
      link.addEventListener("click", this._onClick);
    });

    // Every link dropped: hide the nav so its gap doesn't pad the bar.
    if (!this.querySelector("[data-jump-target]")) this.hidden = true;
  }

  disconnectedCallback() {
    if (!this._onClick) return;
    this.links.forEach((link) => link.removeEventListener("click", this._onClick));
  }

  _resolve(link) {
    const selector = link.getAttribute("data-jump-target");
    if (!selector) return null;
    try {
      return document.querySelector(selector);
    } catch (e) {
      // A malformed selector in the markup shouldn't kill the rest of the nav.
      return null;
    }
  }

  _headerOffset() {
    const header = document.getElementById("site-header-group");
    if (!header) return 0;
    // Only a pinned header covers the target; a scrolled-away one doesn't.
    const rect = header.getBoundingClientRect();
    return rect.top <= 0 ? rect.height : Math.max(0, rect.bottom);
  }

  _handleClick(event) {
    const link = event.currentTarget;
    const target = this._resolve(link);
    if (!target) return;

    event.preventDefault();

    const top =
      target.getBoundingClientRect().top + window.pageYOffset - this._headerOffset() - 8;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }
}

customElements.define("pdp-jump-nav", PdpJumpNav);
